import useCompanyRestClient from "@app-rest/company";
import useAppStore from "@app-store/app";
import useCompanyStore from "@app-store/company";
import useLangStore from "@app-store/lang";
import useAttractionReservationStore from "@app-store/multi-attraction/attraction-reservation";
import { resolveAttractionResKey } from "@app-store/reservation-flux";
import useSystemStore from "@app-store/system";
import { isBowlingPage, isErrorPage } from "@app-utilities/const";
import type { NetworkInformation } from "@app-utilities/interfaces";
import { trackUserEvent } from "@app-utilities/observability";
import { AppInsightsEvent } from "@app-utilities/observability-events";
import { resolveCompanyFirstSystem } from "@app-utilities/system";
import { useObservability } from "@qamf/observability";
import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";

import { notFoundFallbackViewName } from ".";
import { isSkippable, redirectToMaintenanceMode, setFluxMode } from "./helpers";
import { attractionsPages } from "./routes/attractions";
import { maintenanceMode } from "./routes/base";
import { home } from "./routes/reservations";

export default async(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
	const appStore = useAppStore();
	const langStore = useLangStore();
	const systemStore = useSystemStore();
	const companyStore = useCompanyStore();

	if (appStore.azurePopupWindowIsOpen)
		return Promise.resolve();

	async function redirectToNotFound(reason: string, from: RouteLocationNormalized) {
		await langStore.ensureLanguage();
		trackUserEvent(AppInsightsEvent.RedirectToNotFound, { reason, from });
		return next({
			name: "not-found",
			params: to.params,
			query: to.query
		});
	}

	if (to.name === notFoundFallbackViewName)
		trackUserEvent(AppInsightsEvent.RedirectToNotFound, { reason: "vue router not found fallback" });

	if (to.name !== "root" && to.path === from.path)
		return next();

	if (to.name === "root" && appStore.isPWASession() && appStore.defaultPWABaseRoute) {
		return next({
			name: "root-company-system",
			params: {
				...to.params,
				...appStore.defaultPWABaseRoute
			},
			query: to.query
		});
	}

	if (to.name && (isSkippable(to, next) || isErrorPage(to.name))) {
		await langStore.ensureLanguage();
		return next();
	}

	const basicData: Promise<any>[] = [];

	if ("companyId" in to.params) {
		const companyId = +to.params.companyId;
		if (!companyStore.Id || companyStore.Id !== companyId) {
			const companyRestClient = useCompanyRestClient();
			const response = await companyRestClient.info(companyId);
			companyStore.setCompanyData(response.data);
		}

		const { trackTrace } = useObservability();

		trackTrace({
			message: "Prompt: Company info loaded",
			severityLevel: 1
		});

		let systemId: number | null = null;

		if (!("systemId" in to.params)) {
			const resolvedSystem = await resolveCompanyFirstSystem(companyId);

			if (!resolvedSystem || isNaN(resolvedSystem))
				return redirectToNotFound("cannot resolve system", from);

			systemId = resolvedSystem;
			if (!systemId)
				return redirectToNotFound("resolved systemId is falsy", from);

			const nextRoute = to.name && !to.name.toString().startsWith("root") ? to.name : home;
			return next({
				name: nextRoute,
				query: to.query,
				params: { ...to.params, systemId: systemId + "" }
			});
		} else
			systemId = +to.params.systemId;

		const pair = { Id: systemId, CompanyId: companyId };
		const { ensureSystemCompanyPair } = await import("@app-utilities/system");
		const ensureCompanyPairResult = await ensureSystemCompanyPair(pair);

		if (!ensureCompanyPairResult.success) {
			const message = ensureCompanyPairResult.details;
			return redirectToNotFound(message, from);
		}

		if (!systemStore.functionalities)
			return redirectToNotFound("Cannot retrieve functionalities", from);

		if (to.name && (
			to.name.toString() === "reservation-landing"
			|| to.name.toString() === "reservation-attraction-book"
			|| to.name.toString() === "reservation-attraction-offers"
			|| to.name.toString() === "reservation-attraction-offer-detail"
			|| to.name.toString() === "reservation-attraction-cart"
			|| to.name.toString() === "reservation-attraction-thankyou"
		) && !systemStore.functionalities?.MultiAttractions)
			return redirectToNotFound("Missing MultiAttraction functionality", from);

		if (systemStore.info) {
			const companyIdFromSystemInfo = systemStore.info.CompanyId;
			const systemId = systemStore.info.Id;
			if (!systemId || !companyIdFromSystemInfo)
				return redirectToNotFound("missing systemId or companyId from system store", from);

			if (companyId && companyIdFromSystemInfo && companyIdFromSystemInfo !== companyId)
				return redirectToNotFound("companyId param doesn't match companyId in system store", from);

			trackTrace({
				message: "Prompt: System info loaded",
				severityLevel: 1
			});

			if (!to.meta.isStandalonePage) {
				const { ensureWebsiteData } = await import("@app-utilities/system");
				basicData.push(ensureWebsiteData());
			}

			basicData.push((async() => {
				// FIXME: NetworkInformation API is experimental and types are not available - update when API is stable (TS: #47495)
				const appReadyEventData: { networkInformation?: NetworkInformation } = {};
				const extendedNavigator = navigator as Navigator & { connection?: NetworkInformation };
				const navigatorConnection = extendedNavigator.connection;

				if (navigatorConnection) {
					appReadyEventData.networkInformation = {
						downlink: navigatorConnection.downlink ?? null,
						downlinkMax: navigatorConnection.downlinkMax ?? null,
						effectiveType: navigatorConnection.effectiveType ?? null,
						rtt: navigatorConnection.rtt ?? null,
						saveData: navigatorConnection.saveData ?? null,
						type: navigatorConnection.type ?? null
					};
				}

				if (!to.meta.isStandalonePage) {
					await Promise.all([
						systemStore.syncMaintenanceModeActive(),
						companyStore.ensureThemeLoaded(systemId)
					]);
				}

				setFluxMode(to);

				trackUserEvent(AppInsightsEvent.AppReady, appReadyEventData);
				return Promise.resolve();
			})());
		}
	}

	await Promise.all(basicData);
	await langStore.ensureLanguage();
	if (systemStore.maintenanceMode && to.name !== maintenanceMode)
		return redirectToMaintenanceMode(next);

	let preventNext = false;
	if (isBowlingPage(to.name)) {
		const { processBookingRoute } = await import("@app-store/reservation-flux");
		const result = await processBookingRoute(to, from);
		preventNext = result?.preventNext ?? false;
	} else if (to.name && attractionsPages.includes(to.name.toString())) {
		resolveAttractionResKey(to);
		const attractionReservationStore = useAttractionReservationStore();
		await attractionReservationStore.initModuleData();
	}

	if (!preventNext)
		next();
};
